import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { pedirCatalogo, type Catalogo, type FuenteDelCatalogo } from '../../../common/services/catalogo.js';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import { filtrosQueSeOfrecen, type Faceta } from '../filtros.js';

/**
 * El catálogo de estudios.
 *
 * Los filtros que se pintan salen de `filtrosQueSeOfrecen`, no de una lista fija: **un filtro vacío
 * engaña más que no tenerlo** (comprobación 8 del goal). Con el corpus de hoy se ofrecen editor y
 * año; país y población volverán solos cuando la ingesta los rellene.
 */
export function FuentesPage({ idioma }: { readonly idioma: Idioma }) {
  const t = diccionarioDe(idioma).fuentes;
  // `undefined` mientras llega; `null` si no ha llegado. Pintar «0 estudios» mientras carga se lee como un catálogo vacío.
  const [catalogo, setCatalogo] = useState<Catalogo | null | undefined>(undefined);
  const [elegido, setElegido] = useState<Partial<Record<Faceta, string>>>({});
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    void pedirCatalogo().then(setCatalogo);
  }, []);

  const ofrecidos = useMemo(() => (catalogo == null ? [] : filtrosQueSeOfrecen(catalogo.recuentos, catalogo.fuentes.length)), [catalogo]);

  const visibles = useMemo(() => {
    if (catalogo == null) return [];
    return catalogo.fuentes.filter(f => encaja(f, elegido) && contiene(f, busqueda));
  }, [catalogo, elegido, busqueda]);

  return (
    <div className='flex grow flex-col'>
      <main className='mx-auto flex w-full max-w-[1100px] grow flex-col gap-6 px-6 py-10'>
        <div>
          <h1 className='m-0 font-(family-name:--serif) text-[38px] leading-tight font-bold'>{t.titulo}</h1>
          <p className='mt-2 mb-0 font-(family-name:--text) text-[16px] text-(--ink-soft)'>
            {catalogo === undefined ? t.cargando : catalogo === null ? t.sinCatalogo : t.subtitulo.replace('{n}', String(catalogo.fuentes.length))}
          </p>
        </div>

        <div className='flex flex-wrap items-end gap-4'>
          <div className='flex flex-col gap-1'>
            <label htmlFor='buscar' className='font-(family-name:--text) text-[12px] text-(--ink-faint)'>
              {t.buscar}
            </label>
            <input
              id='buscar'
              type='search'
              value={busqueda}
              onChange={e => {
                setBusqueda(e.target.value);
              }}
              className='rounded-md border border-(--line-strong) bg-transparent px-3 py-2 font-(family-name:--text) text-[15px] text-(--ink)'
            />
          </div>

          {ofrecidos.map(faceta => (
            <div key={faceta} className='flex max-w-full min-w-0 flex-col gap-1'>
              <label htmlFor={`f-${faceta}`} className='font-(family-name:--text) text-[12px] text-(--ink-faint)'>
                {t.filtros[faceta]}
              </label>
              <select
                id={`f-${faceta}`}
                value={elegido[faceta] ?? ''}
                onChange={e => {
                  setElegido(a => ({ ...a, [faceta]: e.target.value }));
                }}
                className='w-full max-w-[min(100%,420px)] truncate rounded-md border border-(--line-strong) bg-transparent px-3 py-2 font-(family-name:--text) text-[15px] text-(--ink)'
              >
                <option value=''>{t.todos}</option>
                {(catalogo?.recuentos[faceta] ?? []).map(r => (
                  <option key={r.valor} value={r.valor}>
                    {r.valor} ({String(r.cuantas)})
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {catalogo == null ? null : visibles.length === 0 ? (
          <p className='font-(family-name:--text) text-[15px] text-(--ink-soft)'>{t.sinResultados}</p>
        ) : (
          <table className='w-full table-fixed border-collapse font-(family-name:--text) text-[14px] break-words'>
            <thead>
              <tr className='border-b border-(--line-strong) text-left text-[12px] tracking-[0.04em] text-(--ink-faint) uppercase'>
                <th scope='col' className='py-2 pr-4'>
                  {t.columna.estudio}
                </th>
                <th scope='col' className='py-2 pr-4'>
                  {t.columna.editor}
                </th>
                <th scope='col' className='w-[64px] py-2 pr-4'>
                  {t.columna.anio}
                </th>
                {/* La muestra, entera, está en la ficha. En móvil no cabe una cuarta columna. */}
                <th scope='col' className='hidden py-2 sm:table-cell'>
                  {t.columna.muestra}
                </th>
              </tr>
            </thead>
            <tbody>
              {visibles.map(f => (
                <tr key={f.slug} className='border-b border-(--line)'>
                  <td className='py-3 pr-4'>
                    <Link to={`/fuentes/${f.slug}`} className='text-(--teal-deep)'>
                      {f.tituloOriginal}
                    </Link>
                  </td>
                  <td className='py-3 pr-4 text-(--ink-soft)'>{f.editor}</td>
                  <td className='py-3 pr-4 text-(--ink-soft)'>{f.anio ?? '—'}</td>
                  <td className='hidden py-3 text-(--ink-soft) sm:table-cell'>
                    <span className='line-clamp-3'>{f.muestra ?? '—'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}

/** Un filtro sin elegir no filtra: el valor vacío es "todos", no "ninguno". */
export function encaja(fuente: FuenteDelCatalogo, elegido: Partial<Record<Faceta, string>>): boolean {
  const valorDe: Record<Faceta, string | null> = {
    pais: fuente.pais,
    tipoPoblacion: fuente.tipoPoblacion,
    editor: fuente.editor,
    anio: fuente.anio === null ? null : String(fuente.anio),
  };
  return Object.entries(elegido).every(([faceta, valor]) => valor === undefined || valor === '' || valorDe[faceta as Faceta] === valor);
}

/** Busca en el título y en el editor, que es lo que alguien recuerda de un estudio. */
export function contiene(fuente: FuenteDelCatalogo, busqueda: string): boolean {
  const q = busqueda.trim().toLowerCase();
  if (q === '') return true;
  return `${fuente.tituloOriginal} ${fuente.editor}`.toLowerCase().includes(q);
}
